import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, CardHeader, Button, Input, Alert, ConfirmationModal } from '../../components/ui';
import { organizationAPI } from '../../api';
import { useToast } from '../../contexts/ToastContext';

const defaultConfig = {
  enabled: false,
  client_id: null,
  client_secret: false,
  connected: false,
  connected_email: null,
  calendar_id: null,
};

const GoogleMeetConfigTab = ({ orgId }) => {
  const toast = useToast();
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnect, setShowDisconnect] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [form, setForm] = useState({
    enabled: false,
    client_id: '',
    client_secret: '',
    refresh_token: '',
    calendar_id: '',
  });

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await organizationAPI.getGoogleMeetConfig(orgId);
      setConfig(data);
      setForm((prev) => ({
        ...prev,
        enabled: data.enabled,
        client_id: data.client_id || '',
        calendar_id: data.calendar_id || '',
      }));
    } catch {
      toast.error('Failed to load Google Meet configuration');
    } finally {
      setLoading(false);
    }
  }, [orgId, toast]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  // Reflect the OAuth callback redirect (?googleMeet=connected|error|...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('googleMeet');
    if (!status) return;
    if (status === 'connected') toast.success('Google account connected');
    else if (status === 'invalid_state') toast.error('Google connect failed: invalid or expired request');
    else if (status === 'exchange_failed') toast.error('Google connect failed: could not exchange authorization code');
    else if (status === 'error') toast.error('Google connect was cancelled or failed');
    // Strip the query param so the toast does not re-fire on refresh
    params.delete('googleMeet');
    const q = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
  }, [toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { enabled: form.enabled, calendar_id: form.calendar_id };
      if (form.client_id) payload.client_id = form.client_id;
      if (form.client_secret) payload.client_secret = form.client_secret;
      if (form.refresh_token) payload.refresh_token = form.refresh_token;
      const updated = await organizationAPI.updateGoogleMeetConfig(orgId, payload);
      setConfig(updated);
      setForm((prev) => ({ ...prev, client_secret: '', refresh_token: '' }));
      toast.success('Google Meet configuration saved');
    } catch {
      toast.error('Failed to save Google Meet configuration');
    } finally {
      setSaving(false);
    }
  };

  // The enable toggle persists immediately (no Save click needed).
  const handleToggleEnabled = async () => {
    const next = !form.enabled;
    setForm((f) => ({ ...f, enabled: next })); // optimistic
    setTogglingEnabled(true);
    try {
      const updated = await organizationAPI.updateGoogleMeetConfig(orgId, { enabled: next });
      setConfig(updated);
      setForm((f) => ({ ...f, enabled: updated.enabled }));
      toast.success(next ? 'Google Meet enabled' : 'Google Meet disabled');
    } catch {
      setForm((f) => ({ ...f, enabled: !next })); // revert on failure
      toast.error('Failed to update Google Meet');
    } finally {
      setTogglingEnabled(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      // Return the user to whichever page they connected from (org detail or
      // org settings) instead of the superadmin-only org detail default.
      const { authorize_url } = await organizationAPI.getGoogleMeetConnectUrl(
        orgId,
        window.location.pathname
      );
      window.location.href = authorize_url;
    } catch (err) {
      const msg = err?.response?.data?.error?.message || 'Google Meet OAuth is not configured on this deployment';
      toast.error(msg);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const updated = await organizationAPI.disconnectGoogleMeet(orgId);
      setConfig(updated);
      setForm((prev) => ({ ...prev, refresh_token: '', client_secret: '' }));
      setShowDisconnect(false);
      toast.success('Google account disconnected');
    } catch {
      toast.error('Failed to disconnect Google account');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardBody><p className="text-center text-gray-500 dark:text-gray-400 py-4">Loading…</p></CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Google Meet</h2>
      </CardHeader>
      <CardBody className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white">Enable integration</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              When enabled, lead meetings are created on this org's own Google account.
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggleEnabled}
            disabled={togglingEnabled}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed ${form.enabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {form.enabled && (
          <>
            <hr className="border-gray-200 dark:border-gray-700" />

            {/* Notetaker coupling hint (Open Q7) */}
            <Alert variant="info">
              Connect the <strong>same Google account</strong> that your tl;dv / Recall notetaker
              watches. Meetings are created on this account's calendar, so the notetaker only joins —
              and transcripts only arrive — if it monitors the same account.
            </Alert>

            {/* OAuth connect */}
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Connected account</p>
              {config.connected ? (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-green-600 dark:text-green-400 min-w-0 truncate">
                      ✓ Connected{config.connected_email ? ` — ${config.connected_email}` : ''}
                    </p>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" variant="secondary" onClick={handleConnect} disabled={connecting || disconnecting}>
                        {connecting ? 'Redirecting…' : 'Reconnect'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="!text-red-600 hover:!bg-red-50 dark:hover:!bg-red-900/20"
                        onClick={() => setShowDisconnect(true)}
                        disabled={connecting || disconnecting}
                      >
                        {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                      </Button>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Disconnecting clears this org's stored Google credentials. New Google Meet
                    meetings cannot be scheduled for this organization until an account is
                    reconnected — the integration stays enabled but has no account to create on.
                  </p>
                </>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Not connected</p>
                  <Button size="sm" onClick={handleConnect} disabled={connecting}>
                    {connecting ? 'Redirecting…' : 'Connect Google Account'}
                  </Button>
                </div>
              )}
            </div>

            {/* Calendar ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Calendar ID</label>
              <Input
                placeholder="primary"
                value={form.calendar_id}
                onChange={(e) => setForm((f) => ({ ...f, calendar_id: e.target.value }))}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Defaults to <code className="text-xs bg-gray-100 dark:bg-gray-800 rounded px-1">primary</code>.</p>
            </div>

            {/* Advanced: manual credential entry (fallback when OAuth is unavailable) */}
            <div>
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                onClick={() => setShowAdvanced((v) => !v)}
              >
                {showAdvanced ? 'Hide advanced (manual credentials)' : 'Advanced: enter credentials manually'}
              </button>
              {showAdvanced && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      OAuth Client ID
                      {config.client_id && <span className="ml-2 text-xs text-green-600 dark:text-green-400">(configured)</span>}
                    </label>
                    <Input
                      placeholder="xxxx.apps.googleusercontent.com"
                      value={form.client_id}
                      onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      OAuth Client Secret
                      {config.client_secret && <span className="ml-2 text-xs text-green-600 dark:text-green-400">(configured)</span>}
                    </label>
                    <Input
                      type="password"
                      placeholder={config.client_secret ? 'Leave blank to keep existing' : 'Enter client secret'}
                      value={form.client_secret}
                      onChange={(e) => setForm((f) => ({ ...f, client_secret: e.target.value }))}
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Refresh Token
                      {config.connected && <span className="ml-2 text-xs text-green-600 dark:text-green-400">(configured)</span>}
                    </label>
                    <Input
                      type="password"
                      placeholder={config.connected ? 'Leave blank to keep existing' : 'Enter refresh token'}
                      value={form.refresh_token}
                      onChange={(e) => setForm((f) => ({ ...f, refresh_token: e.target.value }))}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={saving || !form.enabled}>{saving ? 'Saving…' : 'Save Configuration'}</Button>
        </div>
      </CardBody>

      <ConfirmationModal
        isOpen={showDisconnect}
        onClose={() => (disconnecting ? null : setShowDisconnect(false))}
        onConfirm={handleDisconnect}
        title="Disconnect Google Account"
        message="This clears the connected Google account and its stored credentials for this organization. Google Meet will remain enabled but no meetings can be created until a new account is connected. This cannot be undone."
        confirmText="Disconnect"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={disconnecting}
      />
    </Card>
  );
};

export default GoogleMeetConfigTab;
